import { AlertTriangle, Save } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { Daycare, DaycareCreate, DaycareUpdate, MembershipPurchase } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input, Label, Select, Textarea } from '../../components/ui/field';
import { CustomerSearchBox } from '../../components/ui/search';
import { DateRangeInputs, DateShortcutButtons, localDateTimeInput } from '../../components/ui/time-range';
import { formatDate } from '../../shared/format';
import { businessDateInput } from '../../shared/listFilters';
import { api } from '../../shared/api';

export interface DaycareLookups {
  readonly customers?: Array<{ id: string; firstName: string; lastName: string; phone?: string; email?: string }>;
  readonly dogs: Array<{ id: string; customerId: string; name: string }>;
  readonly services: Array<{ id: string; name: string }>;
  readonly memberships?: MembershipPurchase[];
}

interface DaycareFormValues {
  readonly customerId: string;
  readonly dogId: string;
  readonly dogIds: string[];
  readonly startAt: string;
  readonly endAt: string;
  readonly serviceIds: string[];
  readonly dogServiceIds: Record<string, string[]>;
  readonly membershipPurchaseId: string;
  readonly prepaidByMembership: boolean;
  readonly billingMode: 'hourly' | 'entrance-package';
  readonly entranceLimit: string;
  readonly entrancesUsed: number;
  readonly status: Daycare['status'];
  readonly notes: string;
}

const toLocalInputValue = (value?: string) => (value ? toLocalDateTimeInput(new Date(value)) : '');
const toLocalDateTimeInput = localDateTimeInput;
const toIsoDateTime = (value: string) => new Date(value).toISOString();

const defaultValues: DaycareFormValues = {
  customerId: '',
  dogId: '',
  dogIds: [],
  startAt: '',
  endAt: '',
  serviceIds: [],
  dogServiceIds: {},
  membershipPurchaseId: '',
  prepaidByMembership: false,
  billingMode: 'hourly',
  entranceLimit: '',
  entrancesUsed: 0,
  status: 'reserved',
  notes: '',
};

export function availableMembershipForDog(memberships: MembershipPurchase[], customerId: string, dogId: string, date: string) {
  return memberships
    .filter((membership) => membership.customerId === customerId && membership.status === 'active')
    .filter((membership) => membership.startsAt <= date && membership.expiresAt >= date)
    .filter((membership) => membership.dogAllocations.some((allocation) => allocation.dogId === dogId && allocation.remainingDays > 0))
    .sort((first, second) => first.expiresAt.localeCompare(second.expiresAt))[0];
}

export function expiredMembershipForDog(memberships: MembershipPurchase[], customerId: string, dogId: string, date: string) {
  return memberships
    .filter((membership) => membership.customerId === customerId)
    .filter((membership) => membership.status === 'expired' || membership.expiresAt < date)
    .filter((membership) => membership.dogAllocations.some((allocation) => allocation.dogId === dogId && allocation.remainingDays > 0))
    .sort((first, second) => second.expiresAt.localeCompare(first.expiresAt))[0];
}

export function valuesFromDaycare(daycare?: Daycare): DaycareFormValues {
  if (!daycare) return defaultValues;
  return {
    customerId: daycare.customerId,
    dogId: daycare.dogId,
    dogIds: daycare.dogIds?.length ? daycare.dogIds : [daycare.dogId],
    startAt: toLocalInputValue(daycare.startAt),
    endAt: toLocalInputValue(daycare.endAt),
    serviceIds: daycare.serviceIds ?? [],
    dogServiceIds: daycare.dogServiceIds ?? Object.fromEntries((daycare.dogIds?.length ? daycare.dogIds : [daycare.dogId]).map((dogId) => [dogId, daycare.serviceIds ?? []])),
    membershipPurchaseId: daycare.membershipPurchaseId ?? '',
    prepaidByMembership: daycare.prepaidByMembership ?? false,
    billingMode: daycare.billingMode ?? 'hourly',
    entranceLimit: daycare.entranceLimit ? String(daycare.entranceLimit) : '',
    entrancesUsed: daycare.entrancesUsed ?? 0,
    status: normalizeDaycareStatus(daycare.status),
    notes: daycare.notes ?? '',
  };
}

export function daycareCreatePayload(values: DaycareFormValues): DaycareCreate {
  const isMembership = values.prepaidByMembership;
  return {
    customerId: values.customerId,
    dogId: values.dogIds[0] ?? values.dogId,
    dogIds: isMembership ? [values.dogIds[0] ?? values.dogId].filter(Boolean) : values.dogIds,
    startAt: toIsoDateTime(values.startAt),
    endAt: !isMembership && values.endAt ? toIsoDateTime(values.endAt) : undefined,
    serviceIds: isMembership ? [] : serviceIdsFromDogMap(values.dogServiceIds),
    dogServiceIds: isMembership ? {} : values.dogServiceIds,
    membershipPurchaseId: values.membershipPurchaseId || undefined,
    prepaidByMembership: values.prepaidByMembership,
    billingMode: values.billingMode,
    entranceLimit: values.billingMode === 'entrance-package' ? Number(values.entranceLimit || '0') : undefined,
    entrancesUsed: values.billingMode === 'entrance-package' ? values.entrancesUsed : 0,
    notes: values.notes || undefined,
  };
}

export function daycareUpdatePayload(values: DaycareFormValues): DaycareUpdate {
  const isMembership = values.prepaidByMembership;
  return {
    startAt: values.startAt ? toIsoDateTime(values.startAt) : undefined,
    endAt: !isMembership && values.endAt ? toIsoDateTime(values.endAt) : undefined,
    dogIds: isMembership ? [values.dogIds[0] ?? values.dogId].filter(Boolean) : values.dogIds,
    serviceIds: isMembership ? [] : serviceIdsFromDogMap(values.dogServiceIds),
    dogServiceIds: isMembership ? {} : values.dogServiceIds,
    membershipPurchaseId: values.membershipPurchaseId || undefined,
    prepaidByMembership: values.prepaidByMembership,
    billingMode: values.billingMode,
    entranceLimit: values.billingMode === 'entrance-package' ? Number(values.entranceLimit || '0') : undefined,
    entrancesUsed: values.billingMode === 'entrance-package' ? values.entrancesUsed : 0,
    status: values.status,
    notes: values.notes || undefined,
  };
}

export function DaycareFormShell({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">{title}</h1>
        <p className="mt-1 text-sm text-slate-500">Registra visitas cortas por hora, separadas del hotel.</p>
      </div>
      <Card>
        <CardHeader><h2 className="text-sm font-semibold">Datos de guardería</h2></CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

export function DaycareForm({
  initialValue,
  initialDate,
  initialCustomerId,
  initialDogId,
  lookups,
  error,
  isSaving,
  includeStatus = false,
  submitLabel,
  onSubmit,
}: {
  readonly initialValue?: Daycare;
  readonly initialDate?: string;
  readonly initialCustomerId?: string;
  readonly initialDogId?: string;
  readonly lookups: DaycareLookups;
  readonly error?: string;
  readonly isSaving: boolean;
  readonly includeStatus?: boolean;
  readonly submitLabel: string;
  readonly onSubmit: (values: DaycareFormValues) => void;
}) {
  const [values, setValues] = useState<DaycareFormValues>(() => initialValue ? valuesFromDaycare(initialValue) : valuesFromDate(initialDate, initialCustomerId, initialDogId));
  const [flow, setFlow] = useState<'hourly' | 'membership'>(() => initialValue?.prepaidByMembership ? 'membership' : 'hourly');

  useEffect(() => {
    const nextValues = initialValue ? valuesFromDaycare(initialValue) : valuesFromDate(initialDate, initialCustomerId, initialDogId);
    setValues(nextValues);
    setFlow(nextValues.prepaidByMembership ? 'membership' : 'hourly');
  }, [initialCustomerId, initialDate, initialDogId, initialValue]);

  const dogs = values.customerId ? lookups.dogs.filter((dog) => dog.customerId === values.customerId) : [];
  const isMembershipMode = flow === 'membership';
  const membershipDate = values.startAt.slice(0, 10) || businessDateInput();
  const isMembershipCheckedIn = isMembershipMode && Boolean(initialValue?.checkedInAt || ['checked-in', 'checked-out', 'active', 'completed'].includes(String(initialValue?.status ?? '')));
  const selectedDogId = values.dogIds[0] ?? values.dogId;
  const eligibilityQuery = useQuery({
    queryKey: ['membership-eligibility', values.customerId, selectedDogId, membershipDate, values.membershipPurchaseId],
    queryFn: () => api.memberships.eligibility(values.customerId, selectedDogId, membershipDate, values.membershipPurchaseId || undefined),
    enabled: isMembershipMode && !isMembershipCheckedIn && Boolean(values.customerId && selectedDogId && membershipDate),
  });

  useEffect(() => {
    if (!isMembershipMode || !eligibilityQuery.data) return;
    const membershipPurchaseId = eligibilityQuery.data.eligible?.membershipPurchaseId ?? '';
    setValues((current) => current.membershipPurchaseId === membershipPurchaseId ? current : { ...current, membershipPurchaseId });
  }, [eligibilityQuery.data, isMembershipMode]);

  const update = (patch: Partial<DaycareFormValues>) => {
    setValues((current) => ({ ...current, ...patch }));
  };
  const toggleDog = (dogId: string) => {
    setValues((current) => {
      const dogIds = current.dogIds.includes(dogId) ? current.dogIds.filter((id) => id !== dogId) : [...current.dogIds, dogId];
      const nextDogIds = current.prepaidByMembership ? dogIds.slice(-1) : dogIds;
      const dogServiceIds = current.prepaidByMembership ? {} : Object.fromEntries(nextDogIds.map((id) => [id, current.dogServiceIds[id] ?? []]));
      return { ...current, dogIds: nextDogIds, dogId: nextDogIds[0] ?? '', dogServiceIds, membershipPurchaseId: '' };
    });
  };
  const toggleDogService = (dogId: string, serviceId: string) => {
    setValues((current) => {
      const currentServices = current.dogServiceIds[dogId] ?? [];
      const nextServices = currentServices.includes(serviceId) ? currentServices.filter((id) => id !== serviceId) : [...currentServices, serviceId];
      return { ...current, dogServiceIds: { ...current.dogServiceIds, [dogId]: nextServices } };
    });
  };
  const addEstimatedHours = (hours: number) => {
    const startAt = values.startAt || toLocalDateTimeInput(new Date());
    const endAt = toLocalDateTimeInput(new Date(new Date(startAt).getTime() + hours * 3_600_000));
    update({ startAt, endAt });
  };
  const setMembershipStartAt = (startAt: string) => {
    const dogId = values.dogIds[0] ?? values.dogId;
    const membership = availableMembershipForDog(lookups.memberships ?? [], values.customerId, dogId, startAt.slice(0, 10) || businessDateInput());
    update({ startAt, membershipPurchaseId: membership?.id ?? '' });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (isMembershipMode && !values.membershipPurchaseId) return;
    onSubmit(isMembershipMode ? { ...values, prepaidByMembership: true, endAt: '', serviceIds: [], dogServiceIds: {}, dogIds: values.dogIds.slice(0, 1), dogId: values.dogIds[0] ?? values.dogId } : { ...values, prepaidByMembership: false, membershipPurchaseId: '' });
  };

  return (
    <form className="grid gap-3" onSubmit={submit}>
      <div className="grid gap-1.5">
        <Label htmlFor="customerId">Cliente</Label>
        <CustomerSearchBox id="customerId" customers={lookups.customers} value={values.customerId} required disabled={Boolean(initialValue)} placeholder="Selecciona un cliente" onChange={(customerId) => update({ customerId, dogId: '', dogIds: [], dogServiceIds: {}, membershipPurchaseId: '' })} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="daycareFlow">Esquema</Label>
        <Select
          id="daycareFlow"
          value={flow}
          disabled={Boolean(initialValue)}
          onChange={(event) => {
            const nextFlow = event.target.value as 'hourly' | 'membership';
            const dogId = values.dogIds[0] ?? values.dogId;
            const membership = nextFlow === 'membership' ? availableMembershipForDog(lookups.memberships ?? [], values.customerId, dogId, membershipDate) : undefined;
            setFlow(nextFlow);
            update({ membershipPurchaseId: membership?.id ?? '', prepaidByMembership: nextFlow === 'membership', billingMode: 'hourly', endAt: nextFlow === 'membership' ? '' : values.endAt, dogIds: nextFlow === 'membership' ? values.dogIds.slice(0, 1) : values.dogIds, dogServiceIds: nextFlow === 'membership' ? {} : values.dogServiceIds });
          }}
        >
          <option value="hourly">Por hora</option>
          <option value="membership">Membresia</option>
        </Select>
      </div>
      {!isMembershipMode ? (
        <div className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3 text-sm text-[#4f5d49]">
          La guarderia por hora permite varios perros, servicios adicionales, hora de entrada y salida estimada. Se puede cobrar en pagos.
        </div>
      ) : null}
      {isMembershipMode ? <p className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3 text-sm text-[#4f5d49]">La membresia usa los dias pagados asignados al perro. Crea una guarderia de un solo perro, sin servicios, sin salida estimada y sin cobro; consume un dia al registrar entrada.</p> : null}
      <div className="grid gap-1.5">
        <Label>Perros</Label>
        <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3">
          {dogs.length === 0 ? <p className="text-sm text-slate-500">Selecciona un cliente con perros registrados.</p> : null}
          {dogs.map((dog) => {
            const availableMembership = availableMembershipForDog(lookups.memberships ?? [], values.customerId, dog.id, membershipDate);
            const expiredMembership = expiredMembershipForDog(lookups.memberships ?? [], values.customerId, dog.id, membershipDate);
            const expiresAt = availableMembership?.expiresAt;
            const isSelected = selectedDogId === dog.id;
            const verifiedMembership = isSelected ? eligibilityQuery.data?.eligible : undefined;
            const daysUntilExpiration = expiresAt ? Math.ceil((new Date(`${expiresAt}T23:59:59`).getTime() - Date.now()) / 86_400_000) : undefined;
            const isCloseToExpire = daysUntilExpiration !== undefined && daysUntilExpiration >= 0 && daysUntilExpiration <= 7;
            return (
              <label key={dog.id} className="flex flex-wrap items-center gap-2 text-sm text-[#334033]">
                <input type={isMembershipMode ? 'radio' : 'checkbox'} name="daycareDog" checked={values.dogIds.includes(dog.id)} disabled={Boolean(initialValue) || (isMembershipMode && !availableMembership)} onChange={() => isMembershipMode ? update({ dogId: dog.id, dogIds: [dog.id], dogServiceIds: {}, membershipPurchaseId: availableMembership?.id ?? '' }) : toggleDog(dog.id)} />
                <span>{dog.name}</span>
                {isMembershipMode ? availableMembership ? <span className="text-xs text-[#687461]">{verifiedMembership ? `${verifiedMembership.remainingDays} dia(s) disponibles` : isSelected ? 'Verificando pago...' : 'Membresia registrada'}</span> : expiredMembership ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#a33f32]"><AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />Membresia expirada el {formatDate(expiredMembership.expiresAt)}</span> : <span className="text-xs font-medium text-[#8a4a3a]">Sin membresia activa para esta fecha</span> : null}
                {isMembershipMode && isCloseToExpire && expiresAt ? <span className="rounded-full bg-[#fff3d6] px-2 py-0.5 text-xs font-medium text-[#7a5a16]">Vence pronto: {formatDate(expiresAt)}</span> : null}
              </label>
            );
          })}
        </div>
      </div>
      {isMembershipMode ? (
        <div className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
          <div className="grid gap-1.5">
            <Label htmlFor="startAt">Entrada</Label>
            <Input id="startAt" type="datetime-local" required step={60} value={values.startAt} disabled={isMembershipCheckedIn} onChange={(event) => setMembershipStartAt(event.target.value)} />
            <p className="text-xs text-[#687461]">{isMembershipCheckedIn ? 'La entrada ya fue registrada; la fecha queda bloqueada porque ya consumio un dia de membresia.' : 'Solo se registra la entrada planeada. La salida se captura al confirmar salida.'}</p>
          </div>
          {!isMembershipCheckedIn ? <div className="mt-3">
            <DateShortcutButtons unit="hours" onToday={() => setMembershipStartAt(toLocalDateTimeInput(new Date()))} />
          </div> : null}
          {!isMembershipCheckedIn && selectedDogId ? <div className={`mt-3 rounded-md border p-3 text-sm ${eligibilityQuery.data?.eligible ? 'border-[#b9d8b8] bg-[#eff8ee] text-[#2f6b38]' : 'border-[#e5bcb4] bg-[#fff4f2] text-[#8a3f34]'}`}>
            {eligibilityQuery.isLoading ? 'Verificando membresia...' : eligibilityQuery.data?.eligible ? <><span className="font-semibold">Se utilizara {eligibilityQuery.data.eligible.packageName}</span><span className="mt-1 block text-xs">{eligibilityQuery.data.eligible.remainingDays} dia(s) disponibles · vence el {formatDate(eligibilityQuery.data.eligible.expiresAt)}</span></> : eligibilityQuery.data?.message ?? 'No hay una membresia pagada disponible para esta fecha.'}
          </div> : null}
        </div>
      ) : (
        <DateRangeInputs
          mode="datetime"
          start={{ id: 'startAt', required: true, value: values.startAt, onChange: (event) => update({ startAt: event.target.value }) }}
          end={{ id: 'endAt', value: values.endAt, onChange: (event) => update({ endAt: event.target.value }) }}
          startLabel="Entrada"
          endLabel="Salida estimada"
        >
          <div className="grid gap-2 md:grid-cols-2">
            <DateShortcutButtons unit="hours" onToday={() => update({ startAt: toLocalDateTimeInput(new Date()) })} />
            <DateShortcutButtons unit="hours" start={values.startAt} onToday={() => update({ endAt: toLocalDateTimeInput(new Date()) })} onAdd={(endAt) => update({ endAt })} />
          </div>
        </DateRangeInputs>
      )}
      {!isMembershipMode ? <div className="flex flex-wrap gap-2">
        {[1, 2, 3, 5].map((hours) => (
          <Button key={hours} type="button" variant="secondary" className="h-8 px-3" onClick={() => addEstimatedHours(hours)}>
            +{hours} h
          </Button>
        ))}
      </div> : null}
      {!isMembershipMode ? <div className="grid gap-1.5">
        <Label>Servicios por perro</Label>
        <div className="grid gap-3 rounded-md border border-[#d6ded0] bg-white p-3">
          {values.dogIds.length === 0 ? <p className="text-sm text-slate-500">Selecciona uno o más perros primero.</p> : null}
          {lookups.services.length === 0 ? <p className="text-sm text-slate-500">No hay servicios configurados.</p> : null}
          {values.dogIds.map((dogId) => {
            const dog = dogs.find((item) => item.id === dogId);
            const selectedCount = values.dogServiceIds[dogId]?.length ?? 0;
            return (
              <details key={dogId} className="rounded-md bg-[#f8faf6] p-3" open={values.dogIds.length === 1}>
                <summary className="cursor-pointer text-sm font-semibold text-[#334033]">
                  {dog?.name ?? dogId} <span className="font-normal text-[#687461]">({selectedCount} servicio(s))</span>
                </summary>
                <div className="mt-2 grid gap-2">
                {lookups.services.map((service) => (
                  <label key={service.id} className="flex items-center gap-2 text-sm text-[#334033]">
                    <input type="checkbox" checked={(values.dogServiceIds[dogId] ?? []).includes(service.id)} onChange={() => toggleDogService(dogId, service.id)} />
                    {service.name}
                  </label>
                ))}
                </div>
              </details>
            );
          })}
        </div>
      </div> : null}
      {includeStatus ? (
        <div className="grid gap-1.5">
          <Label htmlFor="status">Estado</Label>
          <Select id="status" value={values.status} onChange={(event) => update({ status: event.target.value as Daycare['status'] })}>
            <option value="reserved">Reservada</option>
            <option value="checked-in">Registrada</option>
            <option value="checked-out">Finalizada</option>
          </Select>
        </div>
      ) : null}
      <div className="grid gap-1.5">
        <Label htmlFor="notes">Notas</Label>
        <Textarea id="notes" value={values.notes} onChange={(event) => update({ notes: event.target.value })} />
      </div>
      {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      <Button type="submit" disabled={isSaving || (isMembershipMode && !isMembershipCheckedIn && (!eligibilityQuery.data?.eligible || eligibilityQuery.isFetching))}>
        <Save className="h-4 w-4" aria-hidden="true" />
        {submitLabel}
      </Button>
    </form>
  );
}

function valuesFromDate(date?: string, customerId = '', dogId = ''): DaycareFormValues {
  const start = date ? new Date(`${date}T09:00:00`) : new Date();
  if (Number.isNaN(start.getTime())) return valuesFromDate();
  const end = new Date(start.getTime() + 3 * 3_600_000);
  return {
    ...defaultValues,
    customerId,
    dogId,
    dogIds: dogId ? [dogId] : [],
    dogServiceIds: dogId ? { [dogId]: [] } : {},
    membershipPurchaseId: '',
    startAt: toLocalDateTimeInput(start),
    endAt: toLocalDateTimeInput(end),
  };
}

function serviceIdsFromDogMap(dogServiceIds: Record<string, string[]>) {
  return [...new Set(Object.values(dogServiceIds).flat())];
}

function normalizeDaycareStatus(status: Daycare['status'] | string): Daycare['status'] {
  if (status === 'active') return 'checked-in';
  if (status === 'completed') return 'checked-out';
  if (status === 'checked-in' || status === 'checked-out' || status === 'cancelled') return status;
  return 'reserved';
}
